import { clx } from "@src/components/utils/stringUtils";
import { useAppSelector } from "@src/store/hooks";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { INavLeaf, INavNode } from "./navItems";
import styles from "./NavMenu.module.scss";

const PANEL_GAP = 4;
const SUBMENU_GAP = 4;
const VIEWPORT_MARGIN = 10;
const SUBMENU_CLOSE_DELAY = 150;
const SUBMENU_WIDTH_ESTIMATE = 230;

interface INavMenuProps {
	label: string;
	items: Array<INavNode>;
	isActive?: boolean;
	trigger?: React.ReactNode;
	align?: "left" | "right";
	ariaLabel?: string;
	menuClassName?: string;
	onSelect: (item: INavLeaf) => void;
}

export const NavMenu: React.FC<INavMenuProps> = ({
	label,
	items,
	isActive,
	trigger,
	align = "left",
	ariaLabel,
	menuClassName,
	onSelect,
}) => {
	const theme = useAppSelector((state) => state.settings.theme);
	const [isOpen, setIsOpen] = useState(false);
	const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
	const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0, flipLeft: false });
	const [position, setPosition] = useState({ top: 0, left: 0 });

	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const submenuRef = useRef<HTMLDivElement>(null);
	const submenuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const close = useCallback(() => {
		setIsOpen(false);
		setOpenSubmenuId(null);
	}, []);

	const updatePosition = useCallback(() => {
		const triggerEl = triggerRef.current;
		const panelEl = panelRef.current;
		if (!triggerEl || !panelEl) return;

		const triggerRect = triggerEl.getBoundingClientRect();
		const panelRect = panelEl.getBoundingClientRect();

		let left = align === "right" ? triggerRect.right - panelRect.width : triggerRect.left;
		left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - panelRect.width - VIEWPORT_MARGIN));

		const top = triggerRect.bottom + PANEL_GAP;

		setPosition({ top, left });
	}, [align]);

	useLayoutEffect(() => {
		if (!isOpen) return;
		updatePosition();
	}, [isOpen, updatePosition]);

	useEffect(() => {
		if (!isOpen) return;

		const handle = () => updatePosition();
		window.addEventListener("resize", handle);
		window.addEventListener("scroll", handle, true);
		return () => {
			window.removeEventListener("resize", handle);
			window.removeEventListener("scroll", handle, true);
		};
	}, [isOpen, updatePosition]);

	useEffect(() => {
		if (!isOpen) return;

		const handlePointerDown = (event: MouseEvent) => {
			const target = event.target as Node;
			if (triggerRef.current?.contains(target) || panelRef.current?.contains(target) || submenuRef.current?.contains(target))
				return;
			close();
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") close();
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, close]);

	useEffect(
		() => () => {
			if (submenuCloseTimer.current) clearTimeout(submenuCloseTimer.current);
		},
		[],
	);

	const cancelSubmenuClose = () => {
		if (!submenuCloseTimer.current) return;
		clearTimeout(submenuCloseTimer.current);
		submenuCloseTimer.current = null;
	};

	const scheduleSubmenuClose = () => {
		cancelSubmenuClose();
		submenuCloseTimer.current = setTimeout(() => setOpenSubmenuId(null), SUBMENU_CLOSE_DELAY);
	};

	const openSubmenu = (id: string, anchor: HTMLElement | null) => {
		cancelSubmenuClose();

		const panelEl = panelRef.current;
		if (anchor && panelEl) {
			const anchorRect = anchor.getBoundingClientRect();
			const panelRect = panelEl.getBoundingClientRect();
			const flipLeft = panelRect.right + SUBMENU_WIDTH_ESTIMATE + VIEWPORT_MARGIN > window.innerWidth;

			setSubmenuPosition({
				top: Math.min(anchorRect.top - 6, window.innerHeight - VIEWPORT_MARGIN - 120),
				left: flipLeft ? panelRect.left - SUBMENU_WIDTH_ESTIMATE - SUBMENU_GAP : panelRect.right + SUBMENU_GAP,
				flipLeft,
			});
		}

		setOpenSubmenuId(id);
	};

	const handleSelect = (item: INavLeaf) => {
		if (item.isDisabled) return;
		onSelect(item);
		close();
	};

	const openSubmenuChildren = items.find((item) => item.id === openSubmenuId)?.children;

	useLayoutEffect(() => {
		const submenuEl = submenuRef.current;
		const panelEl = panelRef.current;
		if (!openSubmenuId || !submenuEl || !panelEl || !submenuPosition.flipLeft) return;

		const correctedLeft = panelEl.getBoundingClientRect().left - submenuEl.offsetWidth - SUBMENU_GAP;
		if (Math.abs(correctedLeft - submenuPosition.left) < 1) return;

		setSubmenuPosition((prev) => ({ ...prev, left: correctedLeft }));
	}, [openSubmenuId, submenuPosition.flipLeft, submenuPosition.left]);

	const renderLeaf = (item: INavLeaf, isSubmenuItem: boolean) => (
		<button
			key={item.id}
			type="button"
			disabled={item.isDisabled}
			className={clx(styles.item, isSubmenuItem ? styles.submenuItem : "")}
			onClick={() => handleSelect(item)}>
			<span className={styles.itemLabel}>{item.label}</span>
			{item.isNew ? <span className={styles.newBadge}>New</span> : null}
			{item.badgeText ? (
				<span className={styles.countBadge} title={item.badgeTitle}>
					{item.badgeText}
				</span>
			) : null}
		</button>
	);

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				data-is-active={!!isActive}
				data-is-open={isOpen}
				aria-label={ariaLabel}
				aria-haspopup="true"
				aria-expanded={isOpen}
				className={clx(styles.trigger, trigger ? styles.customTrigger : "")}
				onClick={() => setIsOpen((prev) => !prev)}>
				{trigger ?? (
					<>
						<span className={styles.triggerLabel}>{label}</span>
						<ChevronDown size={14} className={styles.triggerChevron} />
					</>
				)}
			</button>

			{isOpen &&
				createPortal(
					<div className={`theme--${theme}`}>
						<div
							ref={panelRef}
							className={clx(styles.panel, menuClassName ?? "")}
							style={{ top: position.top, left: position.left }}>
							{items.map((item) =>
								item.children?.length ? (
									<button
										key={item.id}
										type="button"
										className={clx(styles.item, styles.submenuTrigger)}
										data-is-open={openSubmenuId === item.id}
										aria-haspopup="true"
										aria-expanded={openSubmenuId === item.id}
										onMouseEnter={(event) => openSubmenu(item.id, event.currentTarget)}
										onMouseLeave={scheduleSubmenuClose}
										onClick={(event) =>
											openSubmenuId === item.id ? setOpenSubmenuId(null) : openSubmenu(item.id, event.currentTarget)
										}>
										<span className={styles.itemLabel}>{item.label}</span>
										<ChevronRight size={14} className={styles.itemChevron} />
									</button>
								) : (
									renderLeaf(item, false)
								),
							)}
						</div>

						{openSubmenuChildren ? (
							<div
								ref={submenuRef}
								className={styles.submenuPanel}
								style={{ top: submenuPosition.top, left: submenuPosition.left }}
								onMouseEnter={cancelSubmenuClose}
								onMouseLeave={scheduleSubmenuClose}>
								{openSubmenuChildren.map((child) => renderLeaf(child, true))}
							</div>
						) : null}
					</div>,
					document.body,
				)}
		</>
	);
};
